import React, { Component } from 'react';
import CustomTable from '../custom/CustomTable';
import { AccessBase, AccessCollaboratorJsonModel } from '../../domain/access/BaseAccess';
import { ColumnProps, ColumnSortParams } from 'primereact/column';
import { customConfirm } from '../custom/CustomPopupConfirm';
import { ConfirmPopup } from 'primereact/confirmpopup';
import { RootState } from '../../redux/store';
import { addPagePath, changePagePath, clearPage } from '../../redux/actions/PageActions';
import { connect, ConnectedProps } from 'react-redux';
import Icon from '../../icons/Icon';
import { User } from '../../domain/rbac/RbacDomains';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../custom/FormicaSearchSelectBox';
import CustomTableContentWrapper from '../custom/CustomTableContentWrapper';
import ComponentName from '../../constant/ComponentName';
import LanguageUtil from '../../util/LanguageUtil';

class AccessListComponent extends Component<Props> {
  public render() {
    const { access } = this.props;
    return (
      <div className={'overflow-y-scroll lg:!max-h-[calc(100vh-27.625rem)] !min-h-[calc(100vh-442px)] scrollable-editor-container w-full flex rounded-lg'}>
        <ConfirmPopup />
        <CustomTable selectionMode='single' columns={this.getColumns()} value={access.collaborators} />
      </div>
    );
  }

  private getColumns = (): ColumnProps[] => {
    return [
      {
        field: 'name',
        header: LanguageUtil.get({ id: 'components.access.table.name', defaultMessage: 'Name' }),
        headerClassName: 'h-[44px] 2xl:h-[52px]',
        className: 'w-[33%]',
        sortFunction: this.getSortNameFunction,
        body: (record: AccessCollaboratorJsonModel) => {
          const darkestColors = ['#3A67FF', '#2D7D5F', '#A36E2A', '#B94D0F', '#015E2F', '#6C8E7A', '#7E6C4F', '#7E8E9F'];
          const randomIndex = Math.floor(Math.random() * darkestColors.length);
          return (
            <div className={'flex gap-x-2 items-center'} data-testid='access_name_text'>
              <div
                style={{ backgroundColor: darkestColors[randomIndex] || '#3A67FF' }}
                className={`flex items-center text-white justify-center text-sm w-6 h-6 rounded-full`}
              >
                {record?.name?.charAt(0)?.toUpperCase() || 'N/A'}
              </div>
              <CustomTableContentWrapper text={this.getName(record)} data-testid={`${ComponentName.ADMIN_USER}_name_text_${record.type}_${record.name}`} />
            </div>
          );
        },
      },
      {
        field: 'type',
        header: LanguageUtil.get({ id: 'components.access.table.type', defaultMessage: 'Type' }),
        headerClassName: 'ml-2',
        className: 'w-[33%] ml-2',
        body: (record: AccessCollaboratorJsonModel) => {
          return (
            <div data-testid='access_type_text'>
              <CustomTableContentWrapper text={record.type} data-testid={`${ComponentName.ADMIN_USER}_type_text_${record.type}_${record.name}`} />
            </div>
          );
        },
      },
      {
        field: 'role',
        header: LanguageUtil.get({ id: 'components.access.table.role', defaultMessage: 'Role' }),
        headerClassName: 'ml-2',
        className: 'w-[20%] ml-2',
        body: (body: AccessCollaboratorJsonModel) => {
          return (
            <div className={'!w-[240px]'}>
              <FormicaSearchSelectBox
                options={this.getRoleOptions()}
                value={body.role}
                ghost
                rounded
                disabled={this.props.disabled}
                onChange={(e) => this.onChangeRole(e, body)}
                data-testid={`${ComponentName.ADMIN_USER}_role_text_${body.type}_${body.name}`}
              />
            </div>
          );
        },
      },
      {
        field: 'action',
        header: LanguageUtil.get({ id: 'components.access.table.action', defaultMessage: 'Action' }),
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        sortable: false,
        body: (record: AccessCollaboratorJsonModel) => {
          return (
            <div
              className={'flex w-full justify-start items-center ml-2'}
              onClick={(e: any) => this.remove(e, record)}
              data-testid={`${ComponentName.ADMIN_USER}_remove_button_${record.type}_${record.name}`}
            >
              <Icon icon={'delete'} size={20} className={'hover:text-primary text-[#607080] hover:cursor-pointer'} />
            </div>
          );
        },
      },
    ];
  };

  private getRoleOptions = () => {
    return [
      { label: LanguageUtil.get({ id: 'common.label.noPermission', defaultMessage: 'No Permission' }), value: 'No Permission' },
      { label: LanguageUtil.get({ id: 'common.label.view', defaultMessage: 'View' }), value: 'View' },
      { label: LanguageUtil.get({ id: 'common.label.edit', defaultMessage: 'Edit' }), value: 'Edit' },
    ];
  };

  private onChangeRole = (e: FormicaSelectBoxItem, body: AccessCollaboratorJsonModel) => {
    const { access, changeAccess } = this.props;
    const index = access.collaborators?.findIndex((a) => a.name == body.name);
    if (index != -1) {
      access.collaborators[index].role = e.value;
      changeAccess(access);
    }
  };

  private remove = (e: any, collaborators: AccessCollaboratorJsonModel) => {
    const { disabled, user, access, changeAccess } = this.props;
    if (disabled || (collaborators.name == user.user.username && collaborators.type == 'User')) return;
    e.stopPropagation();
    e.preventDefault();
    customConfirm({
      target: e.currentTarget,
      message: LanguageUtil.get({ id: 'messages.confirm.removeVariable', defaultMessage: 'Are you sure remove this variable?' }),
      accept: () => {
        const index = access.collaborators?.findIndex((c) => c.name == collaborators.name && c.type == collaborators.type);
        if (index != -1) {
          access.collaborators.splice(index, 1);
          changeAccess(access);
        }
      },
    });
  };

  private getSortNameFunction = ({ data, order }: ColumnSortParams) => {
    return data.sort((data1: AccessCollaboratorJsonModel, data2: AccessCollaboratorJsonModel) => {
      const value1 = this.getName(data1).toLowerCase();
      const value2 = this.getName(data2).toLowerCase();

      if (order === 1) {
        return value1.localeCompare(value2);
      } else if (order === -1) {
        return value2.localeCompare(value1);
      }
    });
  };

  private getName = (record: AccessCollaboratorJsonModel): string => {
    const { users } = this.props;
    let label;
    if (record.type == 'Group') {
      label = record.name;
    } else {
      const _user = users.find((user) => user.username == record.name);
      if (_user) {
        label = this.checkUserName(_user);
      } else {
        label = record.name;
      }
    }
    return label;
  };

  private checkUserName = (user: User) => {
    if (user.firstName != undefined && user.firstName != '' && user.lastName != undefined && user.lastName != '') {
      return user.firstName + ' ' + user.lastName;
    } else {
      return user.username;
    }
  };
}

interface Props extends ReduxProps {
  access: AccessBase;
  changeAccess: (access: AccessBase) => void;
  disabled?: boolean;
  users: User[];
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth,
});
const mapDispatchToProps = { addPagePath, changePagePath, clearPage };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(AccessListComponent);
